/**
 * Milano Wigs — PDP Personalization Panel v2
 * Palo Alto 9.1 + SC Options App Embed
 * v2026-03-27a
 *
 * FIX (v2026-03-27a): SHOP THIS LOOK / SIMPLIFIED OPTION SET SUPPORT
 * - Option sets without a "Personalize It" toggle (e.g., "Shop This Look -
 *   Divine", "Shop This Look - Silk") had their non-cap-size options
 *   (Secure Method, Add Ear Pieces) classified as personalization options
 *   and placed into the accordion panel. Without a toggle, the panel stays
 *   display:none forever — options silently disappear from the PDP.
 * - Added defensive fallback in buildPanel(): if no "Personalize It" SC
 *   option exists AND no .personalize-toggle Liquid snippet is on the page,
 *   ALL remaining options are promoted to always-visible so they render
 *   directly on the PDP. Products with a toggle (e.g., full Divine/Ponytail
 *   sets with 10 options) keep their accordion behavior unchanged.
 *
 * FIX (v2026-03-16a): Quick view enhancement uses price threshold ($150).
 *   Wigs/toppers ($200+) get full enhancement.
 *   Accessories (under $150) keep default Boost quick view.
 *
 * FIX (v2025-03-10b): DOUBLE-INIT GUARD + DEAD CODE CLEANUP
 * - syncStarted was declared inside syncATCPrice() as a local variable,
 *   making the double-call guard ineffective. syncATCPrice() is called
 *   twice on every PDP load (setTimeout 3500ms + waitForBoldOptions path),
 *   producing two concurrent RAF loops and two sets of MutationObservers.
 *   Fix: hoisted _syncStarted to module scope; reset in shopify:section:load
 *   so sync restarts correctly after theme editor section reloads.
 * - Removed unused local variable lastComputedDisplay (declared, never read).
 * - Fixed indentation on theme:cart:added listener (cosmetic).
 *
 * FIX (v2025-03-10a): ATC SAFETY NET + PERF + OVERFLOW GUARD
 * - PERF: Wraps heavy PDP-only init (polling, RAF loop, MutationObservers)
 *   in isPDP guard. Non-PDP pages skip 20s of polling + perpetual RAF.
 *   Quick View and Cart Drawer features still run globally.
 * - ATC SAFETY NET: SC Options hides the original ATC button (bold_hidden,
 *   display:none) and shows a bold_clone copy. If the clone's SC Options
 *   event handler hasn't wired up yet (slow mobile JS), tapping ATC does
 *   nothing. Safety net: a delegated click listener on .bold_clone buttons
 *   sets a 3-second timeout. If theme:cart:added doesn't fire in that
 *   window, it forces the form submit as a fallback.
 * - OVERFLOW GUARD: showSecurePopup sets body overflow:hidden which can
 *   get stuck on mobile if backdrop close fails. Added 15-second auto-
 *   clear failsafe that restores overflow if popup is still open.
 *
 * HOTFIX (v2025-03-06a-hotfix): CART DRAWER AJAX STRIKETHROUGH
 * - Palo Alto wraps drawer in <template>; content not in live DOM on load.
 *   MutationObserver approach failed (nothing to observe at init time).
 * - Fix: Listen for Palo Alto's "theme:cart:added" event (fires after
 *   build() renders items into live DOM), fetch /cart.json, rewrite
 *   .cart__item__price with <ins>/<del> where original_price > final_price.
 * - Also removed data-la-dn-cart-item-row from cart-line-items.liquid to
 *   prevent DN from overwriting per-item prices with base-variant-only values.
 * - Existing Custom CSS (.cart__item__price ins/del) styles the output.
 *
 * FIX (v2025-03-06a): PERCENTAGE DISCOUNT RATIO FOR SC OPTIONS
 * - For percentage-based DN promos (e.g., 25% off sitewide), the old flat
 *   subtraction (SC_total − DN_flat_discount) understated the discount when
 *   SC Options add-ons inflated the price above the base variant.
 * - Now computes discount RATIO from DN's base variant prices (cached on
 *   first read, reset on variant change), then applies that ratio to the
 *   full SC Options total. Matches Shopify Functions checkout behavior.
 * - Example: $1,825 variant + $500 add-ons = $2,325 SC total.
 *   Old: $2,325 − $456.25 (25% of base only) = $1,868.75
 *   New: $2,325 × 0.75 = $1,743.75 (matches checkout)
 * - For flat discounts ($250 off), ratio = $250/$variant gives same result.
 * - Splitit fallback: renders custom "From $X/mo with Splitit" display on
 *   PDP when the Splitit app embed/SDK isn't available. Same approach as
 *   quick view. Updates monthly amount when price changes.
 *
 * FIX (v2025-02-27c): DN DISCOUNT DETECTION — CONFIRMED APPROACH
 * - DN's la:dn:product:discount:calculated event does NOT fire in Dynamic
 *   Pricing mode (confirmed via live testing 2026-03-03).
 * - getDNDiscountCents() parses DN's rendered DOM elements using DN-specific
 *   classes confirmed present in live DOM (.limoniapps-discountninja-
 *   strikethrough, -shortcode-discounted_price). Zero false positives —
 *   Palo Alto's native compare-at uses <s data-compare-price>.
 * - Tries data attributes first (exact cents), falls back to text parsing.
 * - Cached 800ms to avoid redundant DOM reads per sync cycle.
 * - Removed unused API event listener (no IIFE-scoped variables needed).
 *
 * FIX (v2025-02-27b): DN DYNAMIC PRICING INTEGRATION
 * - DN switched from "Embedded Tags" mode (CSS classes) to "Dynamic Pricing"
 *   mode (data-la-dn-* data attributes). Updated DN observer to watch
 *   [data-la-dn-price] container (fallback to old class).
 * - Quick View: adds data-la-dn-price on price element for DN targeting.
 * - DN element finders (getDNDiscountCents, findDNSaleTextEl, findDNStrikeTextEl)
 *   use ONLY DN-specific classes/attributes — never generic <s>/<del> parsing,
 *   which would false-positive match Palo Alto's native compare-at price markup.
 *
 * FIX (v2025-02-27a): DN DYNAMIC PRICING COMPATIBILITY
 * - ATC + cart bar sync is EVENT-DRIVEN when no add-ons are selected
 *   (scTotalCents=0) — DN owns ATC entirely, no flicker.
 * - When SC Options IS active (add-ons selected), RAF loop enforces
 *   our computed price at 200ms because DN Dynamic Pricing continuously
 *   overwrites ATC with base discounted price (ignoring add-on costs).
 *   Checks actual DOM text, not cached value, to detect DN overwrites.
 * - When SC Options is not active (scTotalCents=0), ATC is never touched —
 *   DN owns the ATC button entirely for strikethrough pricing.
 * - RAF loop also handles BNPL monitoring + Splitit recovery (500ms).
 * - No initial doSyncFast() on startup — DN renders first.
 * - Quick view: uses DN's official API (data-la-dn-product-handle +
 *   data-la-dn-product-quickview + la:dn:collection:updated event).
 *
 * FIX (v2025-02-25b): DN + SC OPTIONS PRICE UNIFICATION
 * - DN hides original [data-product-price] inside display:none wrapper.
 *   SC Options updates the hidden element; visible DN price never reflects
 *   add-on costs. ATC/cart-bar/BNPL all sync to wrong (un-discounted) total.
 * - New approach: computeFinalPrice() reads SC Options total (variant+addons)
 *   from the hidden element, subtracts DN discount (read from DN data attrs),
 *   produces the true customer-facing price.
 * - updateDNDisplay() writes the corrected strikethrough (SC total) and sale
 *   price (SC total − discount) into DN's visible elements + data attrs.
 * - All downstream sync (ATC buttons, cart bar, Splitit, Klarna) uses the
 *   computed final price.
 * - DN discount auto-updates on variant change (DN re-renders, RAF re-reads).
 *
 * FIX (v2025-02-25a): DISCOUNT NINJA PDP SUPPORT
 * - findSource: When SC Options is not present, checks for DN's discounted
 *   price element before falling back to regular Palo Alto price.
 *   SC Options still takes priority when present.
 *
 * FIX (v2025-02-16c): BNPL SYNC REWORK + ATC CLONE + UNIVERSAL SYNC
 * - Splitit: Use official API splitit.ui.refresh() per SDK docs. Cache HTML,
 *   restore when Palo Alto section rendering destroys container.
 * - Klarna: Attribute-only update (no refresh-placements). RAF loop keeps
 *   overriding when Klarna's app block reverts on variant change.
 * - ATC price: Target ALL [data-product-price] in ATC area, not just first.
 *   SC Options bold_clone button now gets synced.
 * - syncATCPrice always starts (even without SC Options) for BNPL sync.
 * - findSource falls back to regular price element without SC Options.
 *
 * FIX (v2025-02-16a): FORM CONTAINMENT
 * - .personalize-toggle renders in Palo Alto's "title" block, OUTSIDE the
 *   product <form>. buildPanel() used toggle as DOM anchor → moved all
 *   SC Options inputs outside the form → properties[] never submitted.
 * - Fix: relocate toggle inside the form before using it as anchor.
 * - Safety net: stamp form="<formId>" on all moved inputs.
 *
 * Prior changes (v2025-02-11a):
 * - Hides .bold_option_title inside accordion rows (prevents double title)
 * - Re-extracts option name when conditional options become visible
 * - Hides empty rows (conditional options with no visible form elements)
 * - Moves always-visible options ABOVE the personalize toggle
 * - Styles always-visible options to match Shopify variant selectors
 */

(function() {
  'use strict';

  if (window.MilanoPersonalizeInit) return;
  window.MilanoPersonalizeInit = true;

  var VERSION = 'v2026-03-27a';
  console.log('[Milano] personalize-options.js ' + VERSION + ' loaded');

  // =========================================================================
  // PAGE DETECTION — Skip heavy PDP work on non-product pages
  //
  // This script loads sitewide (needed for Quick View on collections and
  // Cart Drawer strikethrough on all pages). But the heavy initialization
  // — 20s of SC Options polling, RAF price sync loop, MutationObservers —
  // should only run on PDP.
  // =========================================================================

  var isPDP = window.location.pathname.indexOf('/products/') !== -1;
  if (!isPDP) {
    console.log('[Milano] Non-PDP page — skipping panel/sync init');
  }

  // =========================================================================
  // STATE
  // =========================================================================

  var state = {
    container: null,
    panelBuilt: false,
    building: false,
    rows: [],
    currentlyOpen: null,
    panelEl: null,
    contentEl: null,
    summaryEl: null,
    observers: []
  };

  // Module-level guard: prevents syncATCPrice() from initializing twice.
  // Must live here (not inside syncATCPrice) so repeated calls are blocked
  // across executions. Reset by shopify:section:load for theme-editor reloads.
  var _syncStarted = false;

  // =========================================================================
  // CONFIGURATION
  // =========================================================================

  var ALWAYS_VISIBLE_EXACT = [
    'adjust cap size',
    'adjust ponytail cap size',
    'adjust princess cap size',
    'topper base size',
    'fringe topper base size',
    'new cap size',
    'select cap size'
  ];

  var ALWAYS_VISIBLE_KEYWORDS = [
    'fedora', 'baseball caps', 'cap color',
    'lace front options', 'deep lace'
  ];

  // =========================================================================
  // HELP LINKS — "What's This?" popup mapping
  // Maps SC Options names → secure-popup container IDs.
  // Order matters: first match wins. Put specific patterns before general.
  // =========================================================================

  var HELP_LINKS = [
    // Secure methods — product-specific variants (check these first)
    { pattern: /wavy.*secure|secure.*wavy/i, popup: 'wavy-secure-method' },
    { pattern: /onyx.*secure|secure.*onyx/i, popup: 'onyx-secure-method' },
    { pattern: /diamond.*secure|secure.*diamond/i, popup: 'diamond-secure-method' },
    { pattern: /topaz.*secure|emerald.*secure|secure.*topaz|secure.*emerald/i, popup: 'topeme-secure-method' },
    { pattern: /topper.*secure|secure.*topper/i, popup: 'dtoppers-secure-method' },
    // Secure method — generic fallback (Divine, or any unmatched)
    { pattern: /secure method/i, popup: 'secure-method' },

    // Cap size / base size
    { pattern: /base size/i, popup: 'base-size-topaz-emerald' },
    { pattern: /cap size/i, popup: 'new-cap-size' },

    // Lace / front hairline
    { pattern: /lace front|deep lace/i, popup: 'lace-compare' },
    { pattern: /front hairline|blending.*hairline/i, popup: 'front-hairline' },

    // Color
    { pattern: /color service/i, popup: 'color-service' },

    // Cut, style, part
    { pattern: /personalize your cut/i, popup: 'personalize-your-cut-wigs-toppers' },
    { pattern: /signature cut/i, popup: 'milano-signature' },
    { pattern: /style my.*wavy|wavy.*style/i, popup: 'please-style-my-wig-wavy' },
    { pattern: /style my wig|please style/i, popup: 'please-style-my-wig' },
    { pattern: /location of part/i, popup: 'location-of-part' },
    { pattern: /face fram/i, popup: 'face-farming-position' },

    // Ear pieces
    { pattern: /ear piece/i, popup: 'ear-pieces' }
  ];

  function findHelpPopupId(optionName) {
    if (!optionName) return null;
    for (var i = 0; i < HELP_LINKS.length; i++) {
      if (HELP_LINKS[i].pattern.test(optionName)) {
        // Verify the popup container exists on this page
        var el = document.getElementById(HELP_LINKS[i].popup);
        if (el) return HELP_LINKS[i].popup;
      }
    }
    return null;
  }

  function createHelpLink(popupId) {
    var link = document.createElement('a');
    link.href = '#' + popupId;
    link.className = 'milano-help-link';
    link.textContent = 'What\'s This?';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-label', 'Learn more');
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation(); // Don't toggle accordion row
      showSecurePopup(popupId);
    });
    return link;
  }

  // =========================================================================
  // SECURE POPUP — Show / Hide
  // =========================================================================

  function showSecurePopup(id) {
    var container = document.getElementById(id);
    if (!container || !container.classList.contains('pop-up-container')) {
      // Fallback querySelector with safe escaping
      try {
        var escaped = (CSS && CSS.escape) ? CSS.escape(id) : id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
        container = document.querySelector('.pop-up-container#' + escaped);
      } catch (e) { /* ignore */ }
    }
    if (!container) {
      console.warn('[Milano] Popup not found: #' + id);
      return;
    }
    container.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    console.log('[Milano] Popup opened: #' + id);

    // Safety: auto-clear overflow if popup stays open >15s (mobile close can fail)
    setTimeout(function() {
      if (document.body.style.overflow === 'hidden') {
        var stillOpen = document.querySelector('.pop-up-container[style*="flex"]');
        if (stillOpen) {
          hideAllSecurePopups();
          console.log('[Milano] Popup overflow safety — auto-closed after 15s');
        }
      }
    }, 15000);
  }

  function hideAllSecurePopups() {
    var open = document.querySelectorAll('.pop-up-container');
    var closed = false;
    for (var i = 0; i < open.length; i++) {
      if (open[i].style.display === 'flex' || getComputedStyle(open[i]).display === 'flex') {
        open[i].style.display = 'none';
        closed = true;
      }
    }
    if (closed) {
      document.body.style.overflow = '';
      console.log('[Milano] Popups closed');
    }
  }

  // Global close handlers (run once)
  if (!window._milanoPopupHandlers) {
    window._milanoPopupHandlers = true;

    // Close button
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('close-button-img') ||
          e.target.closest('.close-button')) {
        hideAllSecurePopups();
      }
    });

    // Backdrop click (click on .pop-up-container itself, not its children)
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('pop-up-container')) {
        hideAllSecurePopups();
      }
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        hideAllSecurePopups();
      }
    });
  }

  // =========================================================================
  // DOM: Find the correct SC Options container
  // =========================================================================

  function findOptionsContainer() {
    var loaded = document.querySelector('.bold_options.bold_options_loaded');
    if (loaded) return loaded;

    var all = document.querySelectorAll('.bold_options');
    for (var i = 0; i < all.length; i++) {
      if (all[i].querySelector('.bold_option')) return all[i];
    }
    return null;
  }

  // =========================================================================
  // DOM: Find the product <form>
  //
  // SC Options inputs MUST stay inside the <form> for properties[] to submit.
  // Palo Alto 9.1 renders the form via {%- form 'product' -%} which produces
  // <form action="/cart/add" ...>. The .bold_options container lives inside
  // this form, but .personalize-toggle is in the "title" block OUTSIDE it.
  // =========================================================================

  function getProductForm() {
    return document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('.product__form')
        || document.querySelector('[data-product-form]');
  }

  // =========================================================================
  // DOM: Stamp form="" attribute on all inputs inside a container
  //
  // HTML5 form="" attribute associates inputs with a form regardless of DOM
  // position. This is a safety net — even if an element ends up outside the
  // <form>, its inputs will still submit with it.
  // =========================================================================

  function stampFormAttribute(el, formId) {
    if (!el || !formId) return;
    var inputs = el.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].hasAttribute('form')) {
        inputs[i].setAttribute('form', formId);
      }
    }
  }

  // =========================================================================
  // DOM: Extract option name from .bold_option element
  // =========================================================================

  function getOptionName(el) {
    // 1. data-option-name attribute
    var name = el.getAttribute('data-option-name');
    if (name && name.trim()) return name.trim();

    // 2. properties[...] from input name attributes
    var inputs = el.querySelectorAll('select, input, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var n = inputs[i].getAttribute('name') || '';
      var m = n.match(/properties\[([^\]]+)\]/);
      if (m) return m[1].trim();
    }

    // 3. .bold_option_title text
    var title = el.querySelector('.bold_option_title');
    if (title) {
      var text = (title.textContent || '').trim()
        .replace(/\*$/, '').replace(/:$/, '').trim();
      if (text) return text;
    }

    // 4. First label text
    var label = el.querySelector('label');
    if (label) {
      var lt = (label.textContent || '').trim();
      if (lt && lt.length < 80) return lt;
    }

    // 5. Heading
    var heading = el.querySelector('h1,h2,h3,h4,h5,h6,strong,b,.bold_option_header');
    if (heading) return (heading.textContent || '').trim();

    return '';
  }

  // =========================================================================
  // Check if an option element has actual form content
  // (Conditional options may be rendered as empty shells until triggered)
  // =========================================================================

  function hasFormContent(el) {
    var selects = el.querySelectorAll('select');
    var inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    var textInputs = el.querySelectorAll('input[type="text"], textarea');
    var title = el.querySelector('.bold_option_title');
    // Has a visible form element or at least a title
    return (selects.length > 0 || inputs.length > 0 || textInputs.length > 0 || (title && title.textContent.trim()));
  }

  // =========================================================================
  // CLASSIFICATION
  // =========================================================================

  function isAlwaysVisible(name) {
    var lower = name.toLowerCase();
    for (var a = 0; a < ALWAYS_VISIBLE_EXACT.length; a++) {
      if (lower === ALWAYS_VISIBLE_EXACT[a]) return true;
    }
    for (var k = 0; k < ALWAYS_VISIBLE_KEYWORDS.length; k++) {
      if (lower.indexOf(ALWAYS_VISIBLE_KEYWORDS[k]) > -1) return true;
    }
    return false;
  }

  function isPersonalizeIt(name) {
    return name.toLowerCase() === 'personalize it';
  }

  // =========================================================================
  // TOGGLE STATE
  // =========================================================================

  function getPersonalizeState() {
    try {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox) return checkbox.checked;
      var radio = document.querySelector('[data-personalize-input]:checked');
      if (radio) return radio.value === 'Yes';
      var sel = document.querySelector('select[name="properties[Personalize It]"]');
      if (sel) return sel.value === 'Yes';
    } catch (e) {}
    return false;
  }

  function updatePersonalizeState() {
    var isYes = getPersonalizeState();
    document.body.classList.toggle('personalize-yes', isYes);

    if (state.contentEl) {
      if (isYes) {
        state.contentEl.style.display = '';
        state.contentEl.style.maxHeight = '0px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        state.contentEl.style.opacity = '1';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        setTimeout(function() {
          state.contentEl.style.maxHeight = 'none';
          state.contentEl.style.transition = '';
        }, 450);
      } else {
        collapseAllRows();
        state.contentEl.style.transition = 'max-height 0.3s ease, opacity 0.2s ease';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.opacity = '0';
        state.contentEl.style.maxHeight = '0px';
        setTimeout(function() {
          state.contentEl.style.display = 'none';
          state.contentEl.style.transition = '';
          state.contentEl.style.opacity = '';
        }, 350);
      }
    }

    var hidden = document.querySelector('[data-personalize-hidden]');
    if (hidden) hidden.value = isYes ? 'Yes' : 'As Is';

    updateSummary();
  }

  // =========================================================================
  // SELECTION PREVIEW
  // =========================================================================

  function getSelectionPreview(boldOption) {
    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      var text = select.options[select.selectedIndex].text.trim();
      if (text.indexOf('...') > -1 || text.indexOf('Select') > -1 || text === '') {
        return { text: '', hasSelection: false };
      }
      return { text: truncate(stripPrice(text), 28), hasSelection: true };
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      return { text: truncate(stripPrice(rLabel), 28), hasSelection: true };
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    if (checked.length > 0) {
      if (checked.length === 1) {
        var cLabel = findLabelFor(boldOption, checked[0]);
        return { text: truncate(stripPrice(cLabel), 28), hasSelection: true };
      }
      return { text: checked.length + ' selected', hasSelection: true };
    }

    return { text: '', hasSelection: false };
  }

  function findLabelFor(container, input) {
    if (input.id) {
      var label = container.querySelector('label[for="' + input.id + '"]');
      if (label) return label.textContent.trim();
    }
    var parent = input.closest('label');
    if (parent) return parent.textContent.trim();
    var next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL') return next.textContent.trim();
    return input.value || '';
  }

  function stripPrice(text) {
    return text
      .replace(/\s*\(?\+?\$[\d,]+\.?\d{0,2}\s*USD\)?/gi, '')
      .replace(/\s*\$[\d,]+\.?\d{0,2}\s*USD/gi, '')
      .trim();
  }

  function truncate(str, max) {
    if (str.length <= max) return str;
    return str.substring(0, max - 1) + '\u2026';
  }

  // =========================================================================
  // PRICE EXTRACTION
  // =========================================================================

  function extractPrice(text) {
    var match = text.match(/\+?\$?([\d,]+\.?\d{0,2})\s*USD/i);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    match = text.match(/\$(\d+(?:\.\d{2})?)/);
    if (match) return parseFloat(match[1]);
    return 0;
  }

  function getOptionPrice(boldOption) {
    var total = 0;

    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      total += extractPrice(select.options[select.selectedIndex].text);
      return total;
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      total += extractPrice(rLabel);
      return total;
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    checked.forEach(function(cb) {
      var wrapper = cb.closest('.bold_option_value');
      var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
      if (priceEl) {
        total += extractPrice(priceEl.textContent);
      } else {
        var cLabel = findLabelFor(boldOption, cb);
        total += extractPrice(cLabel);
      }
    });

    return total;
  }

  // =========================================================================
  // OPTION ROW
  // =========================================================================

  function createOptionRow(boldOption, name) {
    var row = document.createElement('div');
    row.className = 'milano-option-row';
    row.setAttribute('data-option-name', name);

    // Header
    var header = document.createElement('button');
    header.type = 'button';
    header.className = 'milano-option-row__header';
    header.setAttribute('aria-expanded', 'false');

    var nameSpan = document.createElement('span');
    nameSpan.className = 'milano-option-row__name';
    nameSpan.textContent = name;

    var previewSpan = document.createElement('span');
    previewSpan.className = 'milano-option-row__preview';

    var chevron = document.createElement('span');
    chevron.className = 'milano-option-row__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
        '<path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    header.appendChild(nameSpan);

    // Help link — "What's This?"
    var helpPopupId = findHelpPopupId(name);
    if (helpPopupId) {
      header.appendChild(createHelpLink(helpPopupId));
    }

    header.appendChild(previewSpan);
    header.appendChild(chevron);

    // Body
    var body = document.createElement('div');
    body.className = 'milano-option-row__body';

    boldOption.parentNode.insertBefore(row, boldOption);
    body.appendChild(boldOption);
    row.appendChild(header);
    row.appendChild(body);

    var rowData = {
      row: row,
      header: header,
      body: body,
      nameSpan: nameSpan,
      previewSpan: previewSpan,
      boldOption: boldOption,
      name: name,
      isOpen: false,
      isVisible: true
    };

    // Initial visibility check
    var computedDisplay = window.getComputedStyle(boldOption).display;
    var inlineDisplay = boldOption.style.display;
    var isHidden = inlineDisplay === 'none' || computedDisplay === 'none';
    var isEmpty = !hasFormContent(boldOption);

    if (isHidden || isEmpty) {
      row.classList.add('milano-option-row--hidden');
      rowData.isVisible = false;
    }

    updateRowPreview(rowData);

    // Click handler
    header.addEventListener('click', function(e) {
      e.preventDefault();
      if (rowData.isOpen) {
        collapseRow(rowData);
      } else {
        if (state.currentlyOpen && state.currentlyOpen !== rowData) {
          collapseRow(state.currentlyOpen);
        }
        expandRow(rowData);
      }
    });

    // Watch for input changes
    body.addEventListener('change', function() {
      setTimeout(function() {
        updateRowPreview(rowData);
        updateSummary();
        if (rowData.isOpen) {
          body.style.maxHeight = 'none';
        }
      }, 50);
    });

    return rowData;
  }

  function updateRowPreview(rowData) {
    var preview = getSelectionPreview(rowData.boldOption);
    rowData.previewSpan.textContent = preview.text;
    rowData.row.classList.toggle('milano-option-row--selected', preview.hasSelection);
  }

  function expandRow(rowData) {
    rowData.isOpen = true;
    state.currentlyOpen = rowData;
    rowData.header.setAttribute('aria-expanded', 'true');
    rowData.row.classList.add('milano-option-row--open');

    var body = rowData.body;
    body.style.display = '';
    body.style.maxHeight = '0px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.35s ease';
    body.style.maxHeight = body.scrollHeight + 'px';
    setTimeout(function() {
      body.style.maxHeight = 'none';
      body.style.overflow = 'visible';
      body.style.transition = '';
    }, 380);
  }

  function collapseRow(rowData) {
    rowData.isOpen = false;
    if (state.currentlyOpen === rowData) state.currentlyOpen = null;
    rowData.header.setAttribute('aria-expanded', 'false');
    rowData.row.classList.remove('milano-option-row--open');

    var body = rowData.body;
    body.style.overflow = 'hidden';
    body.style.transition = '';
    body.style.maxHeight = body.scrollHeight + 'px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.3s ease';
    body.style.maxHeight = '0px';
    setTimeout(function() {
      body.style.transition = '';
    }, 350);
  }

  function collapseAllRows() {
    state.rows.forEach(function(rowData) {
      if (rowData.isOpen) collapseRow(rowData);
    });
  }

  // =========================================================================
  // SC OPTIONS CONDITION WATCHER
  // When SC Options shows/hides a conditional option, toggle its row.
  // Also re-extract name for conditional options that had no name initially.
  // =========================================================================

  function watchOptionVisibility(rowData) {
    var boldOption = rowData.boldOption;
    var debounceTimer = null;

    var observer = new MutationObserver(function() {
      // Debounce to avoid rapid-fire from SC Options DOM updates
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        try {
          if (!rowData.row || !rowData.boldOption) return;

          var nowHidden = boldOption.style.display === 'none';
          var nowEmpty = !hasFormContent(boldOption);
          var wasVisible = rowData.isVisible;

          if ((nowHidden || nowEmpty) && wasVisible) {
            rowData.isVisible = false;
            if (rowData.row) rowData.row.classList.add('milano-option-row--hidden');
            if (rowData.isOpen) collapseRow(rowData);
            updateSummary();
          } else if (!nowHidden && !nowEmpty && !wasVisible) {
            // Re-extract name if it was generic
            if (rowData.name.indexOf('Option ') === 0) {
              var newName = getOptionName(boldOption);
              if (newName && newName.indexOf('Option ') !== 0) {
                console.log('[Milano] Renamed "' + rowData.name + '" → "' + newName + '"');
                rowData.name = newName;
                if (rowData.nameSpan) rowData.nameSpan.textContent = newName;
                if (rowData.row) rowData.row.setAttribute('data-option-name', newName);
              }
            }
            rowData.isVisible = true;
            if (rowData.row) {
              rowData.row.classList.remove('milano-option-row--hidden');
              rowData.row.classList.add('milano-option-row--entering');
              setTimeout(function() {
                if (rowData.row) rowData.row.classList.remove('milano-option-row--entering');
              }, 400);
            }
            updateSummary();
          }
        } catch (e) {
          console.log('[Milano] Observer error:', e.message);
        }
      }, 80);
    });

    // Watch style attribute (SC Options sets display) and direct children only
    observer.observe(boldOption, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: false
    });

    state.observers.push(observer);
  }

  // =========================================================================
  // SUMMARY
  // =========================================================================

  function updateSummary() {
    if (!state.summaryEl) return;

    var items = [];
    var total = 0;

    state.rows.forEach(function(rowData) {
      if (!rowData.isVisible) return;

      var preview = getSelectionPreview(rowData.boldOption);
      if (!preview.hasSelection) return;

      var price = getOptionPrice(rowData.boldOption);
      total += price;

      var checked = rowData.boldOption.querySelectorAll('input[type="checkbox"]:checked');
      if (checked.length > 1) {
        checked.forEach(function(cb) {
          var label = stripPrice(findLabelFor(rowData.boldOption, cb));
          var wrapper = cb.closest('.bold_option_value');
          var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
          var p = priceEl ? extractPrice(priceEl.textContent) : extractPrice(findLabelFor(rowData.boldOption, cb));
          if (label && label.toLowerCase() !== 'no color service') {
            items.push({ name: label, price: p });
          }
        });
      } else {
        items.push({ name: rowData.name, value: preview.text, price: price });
      }
    });

    if (items.length === 0) {
      state.summaryEl.classList.remove('milano-summary--visible');
      return;
    }

    state.summaryEl.classList.add('milano-summary--visible');

    var html = '<div class="milano-summary__label">Your Selections</div>';
    html += '<div class="milano-summary__items">';

    items.forEach(function(item) {
      html += '<div class="milano-summary__item">';
      html += '<span class="milano-summary__item-name">';
      if (item.value) {
        html += escapeHtml(item.name) + ': <strong>' + escapeHtml(item.value) + '</strong>';
      } else {
        html += escapeHtml(item.name);
      }
      html += '</span>';
      if (item.price > 0) {
        html += '<span class="milano-summary__item-price">+$' + item.price.toFixed(2) + '</span>';
      }
      html += '</div>';
    });

    html += '</div>';

    if (total > 0) {
      html += '<div class="milano-summary__total">';
      html += '<span>Added services</span>';
      html += '<span>+$' + total.toFixed(2) + '</span>';
      html += '</div>';
    }

    state.summaryEl.innerHTML = html;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // =========================================================================
  // PANEL BUILDER
  // =========================================================================

  function buildPanel() {
    if (state.panelBuilt || state.building) return;

    var container = state.container || findOptionsContainer();
    if (!container) {
      console.log('[Milano] No options container found');
      return;
    }

    var allOptions = container.querySelectorAll('.bold_option');
    if (allOptions.length === 0) {
      console.log('[Milano] No .bold_option elements found');
      return;
    }

    state.building = true;
    console.log('[Milano] Building panel with ' + allOptions.length + ' options...');

    // Classify
    var alwaysVisible = [];
    var personalizeIt = [];
    var personalizationOptions = [];

    allOptions.forEach(function(opt) {
      var name = getOptionName(opt);
      if (isPersonalizeIt(name)) {
        personalizeIt.push(opt);
      } else if (isAlwaysVisible(name)) {
        alwaysVisible.push(opt);
      } else {
        personalizationOptions.push({ el: opt, name: name });
      }
    });

    console.log('[Milano] Always visible: ' + alwaysVisible.length +
      ', Personalize It: ' + personalizeIt.length +
      ', Personalization: ' + personalizationOptions.length);

    // -----------------------------------------------------------------------
    // DEFENSIVE FALLBACK: "Shop This Look" and other simplified option sets
    //
    // Some option sets (e.g., "Shop This Look - Divine", "Shop This Look -
    // Silk") have NO "Personalize It" toggle — neither as an SC Options
    // .bold_option NOR as a .personalize-toggle Liquid snippet. Without
    // either, personalization options get placed into the accordion panel
    // which stays display:none forever. Result: options like Secure Method
    // and Add Ear Pieces silently disappear from the PDP.
    //
    // Fix: if no "Personalize It" option AND no .personalize-toggle exists,
    // promote ALL remaining options to always-visible. If the toggle snippet
    // IS present (e.g., Ponytail Precut with 10 options), the accordion
    // works normally — don't promote.
    // -----------------------------------------------------------------------
    var hasToggle = !!document.querySelector('.personalize-toggle');
    if (personalizeIt.length === 0 && !hasToggle && personalizationOptions.length > 0) {
      console.log('[Milano] No "Personalize It" toggle found (no SC option, no snippet) — promoting ' +
        personalizationOptions.length + ' option(s) to always-visible');
      document.body.classList.add('milano-shop-this-look');
      personalizationOptions.forEach(function(item) {
        alwaysVisible.push(item.el);
      });
      personalizationOptions = [];
    }

    // Hide "Personalize It"
    personalizeIt.forEach(function(opt) {
      opt.style.display = 'none';
    });

    // Always-visible: ensure shown + add styling class + move above toggle
    var toggle = document.querySelector('.personalize-toggle');
    var productForm = getProductForm();
    var formId = productForm ? productForm.id : null;

    // -----------------------------------------------------------------------
    // FORM CONTAINMENT FIX
    //
    // .personalize-toggle renders in Palo Alto's "title" block, OUTSIDE the
    // product <form>. If we insert elements relative to it, SC Options inputs
    // leave the form → properties[] never submit to the cart.
    //
    // Fix: move toggle inside the form (before .bold_options container).
    // Toggle only contains radio inputs that already have form="" attributes
    // (set in personalize-toggle.liquid), so moving it is safe.
    // -----------------------------------------------------------------------
    if (toggle && productForm && !productForm.contains(toggle)) {
      console.log('[Milano] Toggle is outside product form — relocating inside');
      container.parentNode.insertBefore(toggle, container);
    }

    // For Shop This Look products (no toggle), wrap all always-visible options
    // in a layout container so CSS grid can control the layout cleanly.
    var stlWrapper = null;
    if (document.body.classList.contains('milano-shop-this-look') && alwaysVisible.length > 0) {
      stlWrapper = document.createElement('div');
      stlWrapper.className = 'milano-stl-options';
      container.parentNode.insertBefore(stlWrapper, container);
    }

    alwaysVisible.forEach(function(opt) {
      opt.style.display = '';
      opt.classList.add('milano-always-visible');

      // Add "What's This?" help link to the title
      var titleEl = opt.querySelector('.bold_option_title');
      if (titleEl && !titleEl.querySelector('.milano-help-link') && !titleEl.querySelector('.bold-help-link')) {
        var optName = getOptionName(opt);
        var helpId = findHelpPopupId(optName);
        if (helpId) {
          titleEl.appendChild(createHelpLink(helpId));
        }
      }

      // Move above toggle (now inside form), or into STL wrapper, or before container
      if (stlWrapper) {
        stlWrapper.appendChild(opt);
      } else if (toggle && toggle.parentNode) {
        toggle.parentNode.insertBefore(opt, toggle);
      } else {
        container.parentNode.insertBefore(opt, container);
      }
    });

    if (personalizationOptions.length === 0) {
      // Stamp form="" on always-visible inputs (especially for STL wrapper)
      if (formId) {
        alwaysVisible.forEach(function(opt) {
          stampFormAttribute(opt, formId);
        });
      }
      state.building = false;
      state.panelBuilt = true;
      console.log('[Milano] No personalization options — all options always-visible');
      return;
    }

    // Build panel
    var panel = document.createElement('div');
    panel.className = 'milano-panel';

    var content = document.createElement('div');
    content.className = 'milano-panel__content';
    content.style.display = 'none';
    content.style.overflow = 'hidden';

    personalizationOptions.forEach(function(item, idx) {
      var displayName = item.name || 'Option ' + (idx + 1);
      var rowData = createOptionRow(item.el, displayName);
      state.rows.push(rowData);
      content.appendChild(rowData.row);
      watchOptionVisibility(rowData);
      console.log('[Milano]   Row: "' + rowData.name + '"' +
        (rowData.isVisible ? '' : ' (hidden)'));
    });

    // Summary
    var summary = document.createElement('div');
    summary.className = 'milano-summary';
    state.summaryEl = summary;
    content.appendChild(summary);

    panel.appendChild(content);
    state.panelEl = panel;
    state.contentEl = content;

    // Insert panel after toggle (now inside form), or after container
    if (toggle && toggle.parentNode) {
      toggle.parentNode.insertBefore(panel, toggle.nextSibling);
    } else if (container.nextSibling) {
      container.parentNode.insertBefore(panel, container.nextSibling);
    } else {
      container.parentNode.appendChild(panel);
    }

    // Safety net: stamp form="" on all SC Options inputs we moved.
    // Even if DOM structure changes, inputs will submit with the correct form.
    if (formId) {
      stampFormAttribute(panel, formId);
      alwaysVisible.forEach(function(opt) {
        stampFormAttribute(opt, formId);
      });
      console.log('[Milano] Stamped form="' + formId + '" on relocated inputs');
    }

    // Reveal the toggle
    if (toggle) {
      toggle.classList.add('personalize-toggle--ready');
    }

    // Reorder quantity selector into correct position (prevents flash)
    // CSS hides .quanitity_withprice > .selector-wrapper--qty; moving it
    // out of that wrapper makes it visible in its new position.
    var qtyWrapper = document.querySelector('.selector-wrapper--qty');
    var submitHolder = document.querySelector('.product__submit__holder');
    if (qtyWrapper && submitHolder) {
      submitHolder.parentNode.insertBefore(qtyWrapper, submitHolder);
      console.log('[Milano] Quantity selector repositioned');
    }

    state.panelBuilt = true;
    state.building = false;
    console.log('[Milano] Panel built: ' + state.rows.length + ' rows');

    // If already toggled on, show immediately
    if (getPersonalizeState()) {
      state.contentEl.style.display = '';
      state.contentEl.style.maxHeight = 'none';
      state.contentEl.style.opacity = '1';
      document.body.classList.add('personalize-yes');
    }

    updateSummary();

    // Add help links to native Shopify variant selectors (e.g., Lace Front Options)
    addVariantHelpLinks();

    // Re-inject help links when Palo Alto re-renders variant selectors on variant change.
    // Palo Alto replaces the fieldset DOM on variant selection, destroying our injected links.
    var variantArea = document.querySelector('.product__selectors, .product__form .product__block');
    if (variantArea) {
      var helpLinkObserver = new MutationObserver(function() {
        addVariantHelpLinks();
      });
      helpLinkObserver.observe(variantArea, { childList: true, subtree: true });
      state.observers.push(helpLinkObserver);
    }

    // Fallback: Palo Alto section re-renders may replace the observed element itself,
    // orphaning the MutationObserver. Detect variant changes via URL param changes
    // and re-inject help links. Runs every 500ms (negligible cost: 2 querySelectorAll).
    var lastVariantHref = location.href;
    var variantUrlCheck = setInterval(function() {
      if (location.href !== lastVariantHref) {
        lastVariantHref = location.href;
        setTimeout(addVariantHelpLinks, 100); // small delay for DOM to settle
      }
    }, 500);
    // Clean up on section reload
    state._stopVariantUrlCheck = function() { clearInterval(variantUrlCheck); };

    // Auto-open customization if ?customize=true in URL (from quick view link)
    if (window.location.search.indexOf('customize=true') !== -1) {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('[Milano] Auto-opened customization via URL param');
      }
    }
  }

  // =========================================================================
  // VARIANT HELP LINKS
  //
  // Shopify variant selectors (Lace Front Options, etc.) are native <fieldset>
  // elements, not SC Options .bold_option elements. The HELP_LINKS config
  // already has patterns for them — we just need to find the right DOM labels.
  // =========================================================================

  function addVariantHelpLinks() {
    // Palo Alto renders variant labels as:
    //   <legend class="radio__legend">
    //     <span class="radio__legend__label label-typography">Lace Front Options</span>
    //   </legend>
    // OR as popout selects:
    //   <span class="select__label label-typography">Lace Front Options</span>
    var labels = document.querySelectorAll(
      '.radio__legend__label.label-typography, .select__label.label-typography'
    );
    for (var i = 0; i < labels.length; i++) {
      var text = labels[i].textContent.trim();
      var helpId = findHelpPopupId(text);
      if (helpId && !labels[i].querySelector('.milano-help-link')) {
        labels[i].appendChild(createHelpLink(helpId));
        console.log('[Milano] Help link added to variant: "' + text + '" → #' + helpId);
      }
    }
  }

  // =========================================================================
  // INITIALIZATION
  // =========================================================================

  function waitForBoldOptions(callback) {
    var attempts = 0;
    var maxAttempts = 40;

    function check() {
      attempts++;
      var container = findOptionsContainer();
      if (container) {
        var count = container.querySelectorAll('.bold_option').length;
        console.log('[Milano] Found container (' + count + ' options, attempt ' + attempts + ')');
        state.container = container;
        callback();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(check, 500);
      } else {
        console.log('[Milano] Gave up after ' + attempts + ' attempts');
        callback();
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(check, 300);
      });
    } else {
      setTimeout(check, 300);
    }
  }

  // =========================================================================
  // EVENT HANDLERS
  // =========================================================================

  document.addEventListener('change', function(e) {
    var target = e.target;
    if (!target) return;
    try {
      if (target.matches('[data-personalize-input]') ||
          target.matches('select[name="properties[Personalize It]"]') ||
          target.matches('input[name="properties[Personalize It]"]')) {
        updatePersonalizeState();
      }
    } catch (ex) {}
  });

  document.addEventListener('shopify:section:load', function() {
    state.observers.forEach(function(o) { o.disconnect(); });
    state.observers = [];
    if (state._stopPriceSync) state._stopPriceSync();
    if (state._stopVariantUrlCheck) state._stopVariantUrlCheck();
    _syncStarted = false; // allow syncATCPrice() to re-initialize after section reload
    state.panelBuilt = false;
    state.building = false;
    state.container = null;
    state.rows = [];
    state.currentlyOpen = null;

    var existing = document.querySelector('.milano-panel');
    if (existing) existing.remove();

    setTimeout(function() {
      state.container = findOptionsContainer();
      buildPanel();
    }, 500);
  });

  // =========================================================================
  // ATC BUTTON + CART BAR PRICE SYNC
  //
  // Problem: SC Options updates the main price display, but Palo Alto's
  // ATC button and sticky cart bar have their own price spans. Palo Alto's
  // variant/price handlers reset those spans — sometimes by replacing the
  // DOM node, sometimes by rewriting textContent. Cached references go
  // stale and single-fire observers miss the resets.
  //
  // Solution: RAF loop (throttled to ~200ms) that re-queries ALL elements
  // every cycle. Zero cached references = zero stale pointers. Palo Alto
  // can reset prices all it wants; we'll overwrite them next frame.
  //
  // Source:  [data-product-price].bold_option_price_display (SC Options)
  // Target1: .product__submit__add [data-product-price]    (ATC button)
  // Target2: .cart-bar [data-product-price]                (sticky bar)
  // =========================================================================

  // =========================================================================
  // DN DISCOUNT DETECTION — DOM parsing with DN-specific selectors
  //
  // DN's la:dn:product:discount:calculated event does NOT fire in Dynamic
  // Pricing mode (confirmed via live testing). Instead, we parse DN's
  // rendered DOM elements using DN-specific classes that are confirmed
  // present in the live DOM:
  //   .limoniapps-discountninja-strikethrough  (original price)
  //   .limoniapps-discountninja-shortcode-discounted_price  (sale price)
  //
  // These classes are ONLY rendered by DN — Palo Alto's native compare-at
  // uses <s data-compare-price>, so there are zero false positives.
  // =========================================================================

  function syncATCPrice() {

    // ===================================================================
    // DN DISCOUNT — DOM parsing with DN-specific selectors
    //
    // DN's API event does NOT fire in Dynamic Pricing mode (confirmed
    // via live testing 2026-03-03). Parses DN's rendered elements using
    // DN-specific classes confirmed present in live DOM. Tries data
    // attributes first (exact cents), falls back to visible text parsing.
    // Cached 800ms to avoid redundant DOM reads per sync cycle.
    // ===================================================================

    var _dnCache = { cents: null, ts: 0 };

    function getDNDiscountCents() {
      var now = Date.now();
      if (_dnCache.cents !== null && now - _dnCache.ts < 800) return _dnCache.cents;

      var discount = 0;

      var strikeEl = document.querySelector(
        '.limoniapps-discountninja-strikethrough'
      );
      var saleEl = document.querySelector(
        '.limoniapps-discountninja-shortcode-discounted_price'
      );

      if (strikeEl && saleEl) {
        // Try data attributes first (exact cents, no text parsing needed)
        var origAttr = strikeEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');
        var saleAttr = saleEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');

        if (origAttr && saleAttr) {
          var oc = parseInt(origAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
          var sc = parseInt(saleAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
          if (oc > sc && sc > 0) discount = oc - sc;
        }

        // Fallback: parse visible text (Dynamic Pricing may use different attrs)
        if (discount === 0) {
          var origCents = parseCents(strikeEl.textContent);
          var saleCents = parseCents(saleEl.textContent);
          if (origCents > saleCents && saleCents > 0) discount = origCents - saleCents;
        }
      }

      _dnCache = { cents: discount, ts: now };
      return discount;
    }

    function invalidateDNCache() {
      _dnCache.cents = null;
      _dnCache.ts = 0;
    }

    // Returns DN's base variant price in cents (before discount, before add-ons).
    // Cached in _dnVariantBaseCents — set on first read, reset on variant change
    // (DN observer). This avoids re-reading data attrs that updateDNDataAttrs
    // may have modified with add-on-inflated values.
    var _dnVariantBaseCents = 0;
    var _dnDiscountRatio = 0; // cached discount ratio (e.g., 0.25 for 25% off)

    function getDNOriginalPriceCents() {
      if (_dnVariantBaseCents > 0) return _dnVariantBaseCents;

      var strikeEl = document.querySelector('.limoniapps-discountninja-strikethrough');
      if (!strikeEl) return 0;

      // Try data attribute first (exact cents)
      var attr = strikeEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');
      if (attr) {
        _dnVariantBaseCents = parseInt(attr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
      }

      // Fallback: parse text
      if (_dnVariantBaseCents === 0) {
        _dnVariantBaseCents = parseCents(strikeEl.textContent);
      }

      return _dnVariantBaseCents;
    }

    // Returns the DN discount as a ratio (e.g., 0.25 for 25% off).
    // Cached on first computation from DN's base variant prices, reset on
    // variant change. Safe from updateDNDisplay contamination because it
    // uses _dnVariantBaseCents (cached before any writes) and only computes
    // the ratio once per variant.
    function getDNDiscountRatio() {
      if (_dnDiscountRatio > 0) return _dnDiscountRatio;
      var orig = getDNOriginalPriceCents();
      var discount = getDNDiscountCents();
      if (orig > 0 && discount > 0 && discount < orig) {
        _dnDiscountRatio = discount / orig;
        console.log('[Milano] DN discount ratio: ' + (_dnDiscountRatio * 100).toFixed(1) + '% (' + discount + '/' + orig + ' cents)');
      }
      return _dnDiscountRatio;
    }

    function resetDNVariantBase() {
      _dnVariantBaseCents = 0;
      _dnDiscountRatio = 0;
    }

    // ===================================================================
    // PRICE ELEMENT FINDERS
    // ===================================================================

    // SC Options managed price — variant + add-ons (may be hidden by DN)
    function findSCOptionsEl() {
      return document.querySelector('[data-product-price].bold_option_price_display');
    }

    // DN's visible sale price text — uses DN-specific classes only (safe, no false positives)
    function findDNSaleTextEl() {
      return document.querySelector(
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money > .money,' +
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money'
      );
    }

    // DN's visible strikethrough text — uses DN-specific classes only (safe, no false positives)
    function findDNStrikeTextEl() {
      return document.querySelector(
        '.limoniapps-discountninja-strikethrough .limoniapps-discountninja-money > .money,' +
        '.limoniapps-discountninja-strikethrough .limoniapps-discountninja-money'
      );
    }

    // ATC price elements (original + SC Options bold_clone)
    function findATCTargets() {
      var targets = [];
      var seen = [];
      function add(el) {
        if (el && seen.indexOf(el) === -1) { targets.push(el); seen.push(el); }
      }
      var w = document.querySelectorAll('[data-price-wrapper][data-atc-button] [data-product-price]');
      for (var i = 0; i < w.length; i++) add(w[i]);
      var b = document.querySelectorAll('button[name="add"] [data-product-price]');
      for (var j = 0; j < b.length; j++) add(b[j]);
      return targets;
    }

    // Cart bar price
    function findCartBarPriceEl() {
      return document.querySelector('.cart-bar [data-product-price]');
    }

    // ===================================================================
    // PRICE PARSING & FORMATTING
    // ===================================================================

    function parseCents(text) {
      if (!text) return 0;
      var cleaned = (text + '').replace(/[^\d.]/g, '');
      return Math.round(parseFloat(cleaned) * 100) || 0;
    }

    function formatUSD(cents) {
      var d = (cents / 100).toFixed(2);
      var parts = d.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts[0] + '.' + parts[1];
    }

    // ===================================================================
    // COMPUTE FINAL PRICE
    //
    // Priority:
    //  1. SC Options active + DN active → SC_total × (1 − DN_ratio)
    //     Uses discount RATIO (not flat amount) so percentage promos
    //     apply correctly to SC Options total including add-ons.
    //  2. SC Options active, no DN     → SC_total (unchanged)
    //  3. No SC Options, DN active     → DN sale price (already correct)
    //  4. Neither                       → Regular Palo Alto price
    //
    // Returns { finalCents, displayText, scTotalCents, dnDiscountCents }
    // or null if no price source found.
    // ===================================================================

    function computeFinalPrice() {
      var dnDiscount = getDNDiscountCents();
      var scEl = findSCOptionsEl();

      if (scEl) {
        var scCents = parseCents(scEl.textContent);
        if (scCents > 0) {
          // Apply discount as a RATIO of the full SC Options total.
          // This matches Shopify Functions checkout behavior for percentage
          // promos (25% off applies to entire line item including add-ons).
          // For flat discounts, ratio = flat/variant gives equivalent result.
          var appliedDiscount = dnDiscount; // flat fallback
          var ratio = getDNDiscountRatio();
          if (ratio > 0) {
            appliedDiscount = Math.round(scCents * ratio);
          }
          var finalCents = Math.max(0, scCents - appliedDiscount);
          return {
            finalCents: finalCents,
            displayText: formatUSD(finalCents),
            scTotalCents: scCents,
            dnDiscountCents: appliedDiscount
          };
        }
      }

      // No SC Options — check DN sale price (already correct, just read it)
      if (dnDiscount > 0) {
        var dnSaleEl = findDNSaleTextEl();
        if (dnSaleEl) {
          var dnCents = parseCents(dnSaleEl.textContent);
          if (dnCents > 0) {
            return {
              finalCents: dnCents,
              displayText: formatUSD(dnCents),
              scTotalCents: 0,
              dnDiscountCents: dnDiscount
            };
          }
        }
      }

      // Fallback: regular Palo Alto price
      var regEl = document.querySelector(
        '[data-price-wrapper]:not([data-atc-button]) [data-product-price]'
      );
      if (regEl) {
        var regCents = parseCents(regEl.textContent);
        if (regCents > 0) {
          return {
            finalCents: regCents,
            displayText: formatUSD(regCents),
            scTotalCents: 0,
            dnDiscountCents: 0
          };
        }
      }

      return null;
    }

    // ===================================================================
    // UPDATE DN's VISIBLE PRICE DISPLAY
    //
    // When SC Options changes the price (add-ons), DN's rendered
    // strikethrough and sale price are stale. We update them:
    //   Strikethrough → SC Options total (variant + addons, no discount)
    //   Sale price    → SC Options total − DN discount
    //
    // Also updates DN data attributes for currency converters (Geolizr,
    // Doubly). Skips if SC Options hasn't changed anything (scTotalCents=0).
    // ===================================================================

    function updateDNDisplay(priceInfo) {
      if (!priceInfo || priceInfo.scTotalCents <= 0 || priceInfo.dnDiscountCents <= 0) return;

      // Skip when SC Options hasn't added anything above variant price.
      // DN's own display is already correct in this case.
      var dnOrigPrice = getDNOriginalPriceCents();
      if (dnOrigPrice > 0 && priceInfo.scTotalCents <= dnOrigPrice) return;

      weAreWritingDN = true;

      // --- Strikethrough: show SC Options total (what you'd pay without promo) ---
      var strikeEl = findDNStrikeTextEl();
      if (strikeEl) {
        var newStrike = formatUSD(priceInfo.scTotalCents);
        if (strikeEl.textContent !== newStrike) {
          strikeEl.textContent = newStrike;
          updateDNDataAttrs(strikeEl.parentElement, priceInfo.scTotalCents);
        }
      }

      // --- Sale price: show final discounted total ---
      var saleEl = findDNSaleTextEl();
      if (saleEl) {
        var newSale = formatUSD(priceInfo.finalCents);
        if (saleEl.textContent !== newSale) {
          saleEl.textContent = newSale;
          updateDNDataAttrs(saleEl.parentElement, priceInfo.finalCents);
        }
      }

      // Keep flag true for 50ms so MutationObserver callback (async microtask)
      // sees it and skips — prevents our own writes from triggering a 400ms
      // cooldown that would let DN overwrite ATC.
      setTimeout(function() { weAreWritingDN = false; }, 50);
    }

    function updateDNDataAttrs(moneyEl, cents) {
      if (!moneyEl) return;
      var s = cents.toString();
      moneyEl.setAttribute('data-limoniapps-discountninja-price-cents-basecurrency', s);
      moneyEl.setAttribute('data-limoniapps-discountninja-price-cents-presentmentcurrency', s);
      if (moneyEl.hasAttribute('data-geolizr-price')) moneyEl.setAttribute('data-geolizr-price', s);
      if (moneyEl.hasAttribute('doubly-currency-usd')) moneyEl.setAttribute('doubly-currency-usd', s);
    }

    // ===================================================================
    // SYNC FUNCTIONS
    // ===================================================================

    var syncRunning = true;
    var lastSyncedATC = '';
    var lastSyncedBar = '';
    var lastSyncedSplitit = '';
    var splititCachedHTML = '';
    var lastKlarnaSourcePrice = '';
    var dnCooldownUntil = 0; // pause sync while DN re-renders
    var weAreWritingDN = false; // guard: don't cooldown on our own writes

    // --- Fast sync: ATC buttons + cart bar ---
    // ALWAYS writes the correct final price as plain text.
    // DN owns the product price display area (strikethrough formatting).
    // We own the ATC button and cart bar (clean number, no flicker).
    function doSyncFast() {
      try {
        // Skip during DN re-render cooldown to prevent stale reads
        if (Date.now() < dnCooldownUntil) return;

        var p = computeFinalPrice();
        if (!p) return;

        // Update DN's visible product price display when SC Options adds cost
        if (p.scTotalCents > 0 && p.dnDiscountCents > 0) {
          var dnOrigPrice = getDNOriginalPriceCents();
          if (dnOrigPrice > 0 && p.scTotalCents > dnOrigPrice) {
            updateDNDisplay(p);
          }
        }

        var priceText = p.displayText;

        // Write ATC if actual DOM doesn't match our computed price
        var atcTargets = findATCTargets();
        var needsWrite = false;
        for (var i = 0; i < atcTargets.length; i++) {
          if (atcTargets[i].textContent !== priceText) { needsWrite = true; break; }
        }

        if (needsWrite) {
          for (var j = 0; j < atcTargets.length; j++) {
            atcTargets[j].textContent = priceText;
          }
          if (priceText !== lastSyncedATC) {
            console.log('[Milano] ATC synced → ' + priceText);
            lastSyncedATC = priceText;
          }
        }

        // Cart bar price
        var bar = findCartBarPriceEl();
        if (bar && bar.textContent !== priceText) {
          bar.textContent = priceText;
          if (priceText !== lastSyncedBar) {
            console.log('[Milano] Cart bar synced → ' + priceText);
            lastSyncedBar = priceText;
          }
        }
      } catch (e) { /* elements detached — next tick retries */ }
    }

    // --- Slow sync: BNPL (Splitit + Klarna) — only on price change ---
    function doSyncBNPL() {
      try {
        var p = computeFinalPrice();
        if (!p || p.finalCents <= 0) return;

        var amount = p.finalCents / 100; // dollars

        // ----- Splitit -----
        var splititContainer = document.getElementById('splitit-container');
        var splititIsEmpty = splititContainer && splititContainer.innerHTML.length < 10;

        // Cache SDK HTML when first available
        if (splititContainer && splititContainer.innerHTML.length > 10 && !splititCachedHTML) {
          splititCachedHTML = splititContainer.innerHTML;
          console.log('[Milano] Splitit HTML cached (' + splititCachedHTML.length + ' chars)');
        }

        // Restore if destroyed or empty (Palo Alto section re-render)
        if (splititCachedHTML && (!splititContainer || splititIsEmpty)) {
          if (!splititContainer) {
            var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
            if (priceWrapper) {
              var sw = document.createElement('div');
              sw.className = 'splitit-wrapper';
              var sc = document.createElement('div');
              sc.id = 'splitit-container';
              sc.setAttribute('data-splitit', 'true');
              sc.setAttribute('data-splitit-num-installments', '3');
              sc.setAttribute('data-splitit-type', 'product-description');
              sw.appendChild(sc);
              priceWrapper.appendChild(sw);
              splititContainer = sc;
            }
          }
          if (splititContainer) {
            splititContainer.setAttribute('data-splitit-amount', amount.toFixed(2));
            splititContainer.innerHTML = splititCachedHTML;
            var numI = parseInt(splititContainer.getAttribute('data-splitit-num-installments') || '3', 10);
            var perM = (amount / numI).toFixed(2);
            var ps = splititContainer.querySelector('.-splitit---text-price')
                  || splititContainer.querySelector('.-splitit--text-price')
                  || splititContainer.querySelector('.splitit--text-price');
            if (ps) ps.textContent = '$' + perM;
            try {
              if (window.splitit && window.splitit.ui && typeof window.splitit.ui.refresh === 'function') {
                window.splitit.ui.refresh();
              }
            } catch (e2) { /* ignore */ }
            console.log('[Milano] Splitit restored: $' + amount.toFixed(2) + ' ($' + perM + '/mo)');
            lastSyncedSplitit = p.displayText;
          }
        }

        // Update when container exists and is populated
        if (splititContainer && !splititIsEmpty) {
          var oldAmount = parseFloat(splititContainer.getAttribute('data-splitit-amount') || '0');
          if (Math.abs(amount - oldAmount) > 0.01) {
            splititContainer.setAttribute('data-splitit-amount', amount.toFixed(2));
            try {
              if (window.splitit && window.splitit.ui && typeof window.splitit.ui.refresh === 'function') {
                window.splitit.ui.refresh();
              }
            } catch (e2) { /* ignore */ }
            var numInstallments = parseInt(splititContainer.getAttribute('data-splitit-num-installments') || '3', 10);
            var perMonth = (amount / numInstallments).toFixed(2);
            var priceSpan = splititContainer.querySelector('.-splitit---text-price')
                         || splititContainer.querySelector('.-splitit--text-price')
                         || splititContainer.querySelector('.splitit--text-price');
            if (priceSpan) priceSpan.textContent = '$' + perMonth;
            console.log('[Milano] Splitit synced: $' + oldAmount.toFixed(2) + ' → $' + amount.toFixed(2) + ' ($' + perMonth + '/mo)');
            lastSyncedSplitit = p.displayText;
          }
        }

        // Fallback: No SDK container and no cached HTML — render custom Splitit
        // display (same approach as quick view). Works without app embed.
        if (!splititContainer && !splititCachedHTML) {
          var fallback = document.querySelector('.milano-splitit-fallback');
          var perMo = (amount / 3).toFixed(2);
          var splititLogoUrl = 'https://splitit.com/wp-content/themes/starter/images/logo/full-green.svg';
          // Try to find logo from existing Splitit instances
          var existingLogo = document.querySelector('.splitit-wrapper img, .milano-qv-splitit img');
          if (existingLogo && existingLogo.src) splititLogoUrl = existingLogo.src;

          if (!fallback) {
            var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
            if (priceWrapper) {
              fallback = document.createElement('div');
              fallback.className = 'milano-splitit-fallback';
              fallback.style.cssText = 'display:flex;align-items:center;gap:4px;flex-wrap:wrap;font-size:13px;color:#888;line-height:1.4;margin:6px 0 12px;padding:0;';
              priceWrapper.appendChild(fallback);
            }
          }

          if (fallback) {
            var newHtml = 'From <strong style="color:#333">$' + perMo + '</strong>/month with ' +
              '<img src="' + splititLogoUrl + '" alt="Splitit" style="height:18px;vertical-align:middle;" onerror="this.outerHTML=\'<strong style=color:#2fad4a>Splitit</strong>\'">' +
              ' <a href="https://www.splitit.com/" target="_blank" rel="noopener" style="color:#888;text-decoration:underline;margin-left:2px;font-size:13px;">Learn more</a>';
            if (p.displayText !== lastSyncedSplitit) {
              fallback.innerHTML = newHtml;
              console.log('[Milano] Splitit fallback: $' + amount.toFixed(2) + ' ($' + perMo + '/mo)');
              lastSyncedSplitit = p.displayText;
            }
          }
        }

        // ----- Klarna -----
        if (p.displayText !== lastKlarnaSourcePrice) {
          lastKlarnaSourcePrice = p.displayText;
          var amountCents = Math.round(amount * 100);

          var setKlarnaAmount = function() {
            var el = document.querySelector('klarna-placement[data-purchase-amount]');
            if (el) el.setAttribute('data-purchase-amount', amountCents.toString());
          };

          setKlarnaAmount();
          setTimeout(setKlarnaAmount, 500);
          setTimeout(setKlarnaAmount, 1200);

          console.log('[Milano] Klarna synced to ' + amountCents + ' cents ($' + amount.toFixed(2) + ')');
        }
      } catch (e) { /* elements detached — next tick retries */ }
    }

    // ===================================================================
    // RAF LOOP — Throttled polling
    // ===================================================================
    // We always own ATC + cart bar (plain text, correct final price).
    // DN owns the product price display area (strikethrough formatting).
    // RAF at 200ms enforces our price against DN's continuous overwrites.
    // BNPL monitoring at 500ms. Splitit recovery at 2000ms.
    // ===================================================================

    var lastRun = 0;
    var lastFastRun = 0;
    var lastSplititCheck = 0;
    var lastRAFDisplay = '';

    function rafLoop() {
      if (!syncRunning) return;
      var now = Date.now();

      // Enforce ATC + cart bar every 200ms (fights DN overwrites)
      if (now - lastFastRun >= 200) {
        doSyncFast();
        lastFastRun = now;
      }

      if (now - lastRun >= 500) {
        // Trigger BNPL sync on price change (non-destructive read)
        var p = computeFinalPrice();
        var curDisplay = p ? p.displayText : '';
        if (curDisplay && curDisplay !== lastRAFDisplay) {
          lastRAFDisplay = curDisplay;
          doSyncBNPL();
        }
        lastRun = now;
      }

      // Periodic Splitit existence check
      if (now - lastSplititCheck >= 2000) {
        var sc = document.getElementById('splitit-container');
        if (splititCachedHTML && (!sc || sc.innerHTML.length < 10)) {
          doSyncBNPL();
        }
        lastSplititCheck = now;
      }

      requestAnimationFrame(rafLoop);
    }

    // ===================================================================
    // OBSERVERS — Watch SC Options + DN for changes
    // ===================================================================

    function observeSources() {
      var bnplDebounce = null;
      function onPriceChange() {
        invalidateDNCache(); // DN may have re-rendered
        doSyncFast();
        clearTimeout(bnplDebounce);
        bnplDebounce = setTimeout(doSyncBNPL, 150);
      }

      // Watch SC Options price element
      var scEl = findSCOptionsEl();
      if (scEl) {
        var obs1 = new MutationObserver(onPriceChange);
        obs1.observe(scEl, { childList: true, characterData: true, subtree: true });
        state.observers.push(obs1);
        if (scEl.parentElement) {
          var obs2 = new MutationObserver(onPriceChange);
          obs2.observe(scEl.parentElement, { childList: true, subtree: true });
          state.observers.push(obs2);
        }
      }

      // Watch DN container for re-renders (variant change triggers full replace)
      // Dynamic Pricing: DN targets [data-la-dn-price] elements
      // Old mode fallback: .limoniapps-discountninja-productprice-dynamic
      var dnContainer = document.querySelector('[data-la-dn-price] [data-price-wrapper]')
                     || document.querySelector('[data-la-dn-price]')
                     || document.querySelector('.limoniapps-discountninja-productprice-dynamic');
      if (dnContainer) {
        var dnDebounce = null;
        var obs3 = new MutationObserver(function() {
          if (weAreWritingDN) return;
          invalidateDNCache();
          resetDNVariantBase(); // variant changed → re-read base price
          dnCooldownUntil = Date.now() + 400;
          clearTimeout(dnDebounce);
          dnDebounce = setTimeout(function() {
            dnCooldownUntil = 0;
            invalidateDNCache();
            onPriceChange();
          }, 400);
        });
        obs3.observe(dnContainer, { childList: true, subtree: true });
        state.observers.push(obs3);
      }

      // Watch the data-price-wrapper in case Palo Alto replaces the whole block
      var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
      if (priceWrapper && priceWrapper !== (scEl && scEl.parentElement)) {
        var obs4 = new MutationObserver(onPriceChange);
        obs4.observe(priceWrapper, { childList: true, subtree: true });
        state.observers.push(obs4);
      }
    }

    // ===================================================================
    // START
    // ===================================================================

    if (_syncStarted) return;
    _syncStarted = true;
    console.log('[Milano] Price sync starting (DN-aware v2025-03-10b)');

    observeSources();
    // NOTE: No initial doSyncFast() — let DN render ATC pricing first.
    // ATC sync is event-driven: triggers when SC Options changes or DN re-renders.
    doSyncBNPL();
    requestAnimationFrame(rafLoop);

    state._stopPriceSync = function() { syncRunning = false; };
  }

  // =========================================================================
  // CART DRAWER — AJAX STRIKETHROUGH FIX (v2026-03-09)
  //
  // Problem: Palo Alto wraps the drawer in <template data-cart-drawer-template>.
  // Template content is NOT in the live DOM until renderCartDrawer() clones it.
  // After cloning, Palo Alto fetches section HTML via the section rendering API,
  // but the Shopify Function discount hasn't been applied yet, so
  // original_price == final_price and Liquid never renders <ins>/<del>.
  //
  // Fix: Listen for Palo Alto's "theme:cart:added" custom event (fires after
  // build() renders items into the live DOM), then fetch /cart.json (always
  // has correct prices) and rewrite .cart__item__price with <ins>/<del>.
  // =========================================================================

  (function() {
    var _fixing = false;

    function fmtMoney(cents) {
      var d = (cents / 100).toFixed(2);
      var parts = d.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts[0] + '.' + parts[1];
    }

    function fixDrawerPrices() {
      if (_fixing) return;
      _fixing = true;

      fetch('/cart.json', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          if (!cart || !cart.items || cart.items.length === 0) return;

          var map = {};
          for (var i = 0; i < cart.items.length; i++) {
            map[cart.items[i].key] = cart.items[i];
          }

          var rows = document.querySelectorAll('.cart__item[data-item]');
          var fixed = 0;

          for (var j = 0; j < rows.length; j++) {
            var row = rows[j];
            var key = row.getAttribute('data-item');
            var item = map[key];
            if (!item) continue;

            if (item.original_price > item.final_price) {
              var priceEl = row.querySelector('.cart__item__price');
              if (!priceEl) continue;
              if (priceEl.querySelector('ins')) continue;

              priceEl.innerHTML = '<ins>' + fmtMoney(item.final_price) + '</ins><del>' + fmtMoney(item.original_price) + '</del>';
              fixed++;
            }
          }

          if (fixed > 0) {
            console.log('[Milano] Drawer strikethrough fixed: ' + fixed + ' item(s)');
          }
        })
        .catch(function(e) {
          console.warn('[Milano] Drawer price fix failed:', e);
        })
        .then(function() {
          _fixing = false;
        });
    }

    // Palo Alto fires "theme:cart:added" after build() renders items into live DOM.
    // This works regardless of whether the <template> has been cloned yet.
    document.addEventListener('theme:cart:added', function() {
      var attempts = 0;
      function tryFix() {
        if (attempts >= 5) return;
        attempts++;
        fixDrawerPrices();
        setTimeout(tryFix, 1500);
      }
      setTimeout(tryFix, 1000);
    });

    console.log('[Milano] Drawer strikethrough listener registered (theme:cart:added)');
  })();

  // =========================================================================
  // QUICK VIEW — Funnel to PDP for cap size + customization
  // Boost SD V3 renders quick view via JS. Cap sizes vary per product
  // (15 SC Options products) — can't replicate in modal. Strategy:
  // hide ATC + quantity, match PDP visual hierarchy, add Splitit, funnel to PDP.
  // =========================================================================
  (function() {
    var qvStyleId = 'milano-qv-styles';
    if (!document.getElementById(qvStyleId)) {
      var s = document.createElement('style');
      s.id = qvStyleId;
      s.textContent =
        '.milano-qv-hide { display:none!important; }' +

        /* Splitit BNPL line */
        '.milano-qv-splitit {' +
        '  display:flex; align-items:center; gap:4px; flex-wrap:wrap;' +
        '  font-size:13px; color:#888; line-height:1.4;' +
        '  margin:6px 0 12px; padding:0;' +
        '}' +
        '.milano-qv-splitit img {' +
        '  height:18px; vertical-align:middle;' +
        '}' +
        '.milano-qv-splitit a {' +
        '  color:#888; text-decoration:underline; margin-left:2px; font-size:13px;' +
        '}' +

        /* Stock indicator */
        '.milano-qv-stock {' +
        '  display:flex; align-items:center; gap:6px;' +
        '  font-size:13px; color:#555; margin:0 0 12px; padding:0;' +
        '}' +
        '.milano-qv-stock-dot {' +
        '  width:7px; height:7px; border-radius:50%;' +
        '  background:#4caf50; flex-shrink:0;' +
        '}' +

        /* Divider */
        '.milano-qv-divider {' +
        '  border:none; border-top:1px solid #e8e6e3;' +
        '  margin:12px 0;' +
        '}' +

        /* Primary CTA */
        '.milano-qv-cta {' +
        '  display:block!important; width:100%; text-align:center;' +
        '  padding:14px 20px; border-radius:8px; box-sizing:border-box;' +
        '  font-size:15px; font-family:Inter,sans-serif; font-weight:600;' +
        '  letter-spacing:0.4px; text-decoration:none!important;' +
        '  color:#fff!important; background:#c5a02e;' +
        '  position:relative; overflow:hidden; margin:0;' +
        '  transition:transform 0.15s ease, box-shadow 0.15s ease;' +
        '}' +
        '.milano-qv-cta:hover {' +
        '  transform:translateY(-1px);' +
        '  box-shadow:0 4px 12px rgba(197,160,46,0.35);' +
        '}' +
        '.milano-qv-cta:active { transform:translateY(0); }' +
        '@keyframes milanoShimmer {' +
        '  0% { transform:translateX(-100%); }' +
        '  100% { transform:translateX(100%); }' +
        '}' +
        '.milano-qv-cta::after {' +
        '  content:""; position:absolute; top:0; left:0;' +
        '  width:100%; height:100%;' +
        '  background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);' +
        '  animation:milanoShimmer 3s ease-in-out infinite;' +
        '  pointer-events:none;' +
        '}' +

        /* Trust row */
        '.milano-qv-trust {' +
        '  display:flex; justify-content:center; gap:14px; flex-wrap:wrap;' +
        '  margin:12px 0 0; padding:12px 0 0;' +
        '  border-top:1px solid #eee;' +
        '}' +
        '.milano-qv-trust span {' +
        '  font-size:11px; color:#777; font-family:Inter,sans-serif;' +
        '  letter-spacing:0.2px; white-space:nowrap;' +
        '}' +

        /* Discount Ninja in Boost modal */
        '.boost-sd__modal [class*="limoniapps"] { display:inline!important; }' +

        /* Vertically center the info column in quick view */
        '.boost-sd__modal .boost-sd__quick-view { align-items:center; }' +
        '.boost-sd__modal [class*="boost-sd__quick-view-info"],' +
        '.boost-sd__modal [class*="boost-sd__product-info"] {' +
        '  display:flex; flex-direction:column; justify-content:center;' +
        '}';
      document.head.appendChild(s);
    }

    function hideElement(el) {
      if (el) el.classList.add('milano-qv-hide');
    }

    function extractPrice(el) {
      var text = (el.textContent || '').replace(/[^0-9.,]/g, '').replace(/,/g, '');
      var num = parseFloat(text);
      return isNaN(num) ? 0 : num;
    }

    function formatMoney(n) {
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    // Find Splitit logo URL from the PDP's existing rendered instance
    function getSplititLogoUrl() {
      var pdpLogo = document.querySelector('#splitit-container img, .splitit-wrapper img');
      if (pdpLogo) return pdpLogo.src;
      // Fallback: Splitit CDN
      return 'https://splitit.com/wp-content/themes/starter/images/logo/full-green.svg';
    }

    function enhanceQuickView() {
      var modal = document.querySelector('.boost-sd__modal[aria-modal="true"]') ||
                  document.querySelector('.boost-sd__quick-view');
      if (!modal) return false;

      var detailLink = modal.querySelector('.boost-sd__quick-view-detail-link') ||
                       modal.querySelector('.boost-sd__quick-view-btn a[href*="/products/"]') ||
                       modal.querySelector('a[href*="/products/"]');
      if (!detailLink) return false;
      var productHref = detailLink.getAttribute('href') || '';
      if (modal.getAttribute('data-milano-qv-product') === productHref) return true;

      // Only enhance wigs & toppers — accessories keep default Boost quick view
      // Price-based: wigs/toppers are $200+, all accessories are under $100
      // Gift cards and iBands excluded regardless of price
      var hrefLower = productHref.toLowerCase();
      if (hrefLower.indexOf('gift-card') !== -1 || hrefLower.indexOf('iband') !== -1) {
        modal.setAttribute('data-milano-qv-product', productHref);
        return true;
      }
      var priceEl = modal.querySelector('[class*="boost-sd__product-price"], [class*="price"]');
      var productPrice = 0;
      if (priceEl) {
        var priceMatch = (priceEl.textContent || '').match(/\$[\d,]+\.?\d*/);
        if (priceMatch) productPrice = parseFloat(priceMatch[0].replace(/[$,]/g, '')) || 0;
      }
      if (productPrice < 150) {
        modal.setAttribute('data-milano-qv-product', productHref);
        return true;
      }

      // --- Find the right-side content area (text column) ---
      var contentArea = modal.querySelector('[class*="boost-sd__quick-view-info"]') ||
                        modal.querySelector('[class*="boost-sd__product-info"]') ||
                        detailLink.parentElement;

      // 1. Hide ATC button
      var buttons = modal.querySelectorAll('button');
      for (var b = 0; b < buttons.length; b++) {
        if ((buttons[b].textContent || '').toLowerCase().indexOf('add to cart') !== -1) {
          hideElement(buttons[b]);
          break;
        }
      }

      // 2. Hide quantity
      var qtyByClass = modal.querySelectorAll(
        '[class*="boost-sd__product-quantity"],' +
        '[class*="boost-sd__quantity"],' +
        '[class*="quantity-selector"]'
      );
      for (var q = 0; q < qtyByClass.length; q++) {
        hideElement(qtyByClass[q]);
      }
      var numInputs = modal.querySelectorAll('input[type="number"]');
      for (var n = 0; n < numInputs.length; n++) {
        var wrap = numInputs[n].parentElement;
        for (var w = 0; w < 2 && wrap && wrap !== modal; w++) {
          if (wrap.querySelector('input[type="number"]') &&
              wrap.querySelectorAll('button').length === 2) {
            hideElement(wrap);
            var prev = wrap.previousElementSibling;
            if (prev && prev.children.length === 0 &&
                (prev.textContent || '').trim() === 'Quantity') {
              hideElement(prev);
            }
            break;
          }
          wrap = wrap.parentElement;
        }
      }

      // 3. Hook Discount Ninja into the quick view via official API
      //    Dynamic Pricing: data-la-dn-price on price element
      //    Quick View: data-la-dn-product-handle + data-la-dn-product-quickview on container
      var productHandle = extractHandleFromHref(productHref);
      if (productHandle) {
        // Mark modal for DN
        modal.setAttribute('data-la-dn-product-handle', productHandle);
        modal.setAttribute('data-la-dn-product-quickview', '');

        // Mark price element for DN Dynamic Pricing
        var priceEl = modal.querySelector('[class*="boost-sd__product-price"], [class*="price"]');
        if (priceEl) {
          priceEl.setAttribute('data-la-dn-price', '');
          // Keep old classes for backwards compatibility with embedded tags mode
          priceEl.classList.add('limoniapps-discountninja-productprice');
          priceEl.classList.add('limoniapps-discountninja-productprice-dynamic');
        }
      }

      // Inject Splitit after price (uses whatever price is displayed, DN will update later)
      var priceEl = modal.querySelector('[class*="boost-sd__product-price"], [class*="price"]');
      if (priceEl && !modal.querySelector('.milano-qv-splitit')) {
        var price = extractPrice(priceEl);
        if (price > 0) {
          var monthly = formatMoney(price / 3);
          var logoUrl = getSplititLogoUrl();
          var splitit = document.createElement('div');
          splitit.className = 'milano-qv-splitit';
          splitit.innerHTML =
            'From ' + monthly + '/month with ' +
            '<img src="' + logoUrl + '" alt="Splitit" onerror="this.outerHTML=\'<strong style=color:#2fad4a>Splitit</strong>\'">' +
            ' <a href="https://www.splitit.com/" target="_blank" rel="noopener">Learn more</a>';
          priceEl.parentElement.insertBefore(splitit, priceEl.nextSibling);
        }
      }

      // 4. Inject stock indicator — insert before the CTA zone
      //    Find reviews element as anchor (it rendered in the screenshot)
      if (!modal.querySelector('.milano-qv-stock')) {
        var reviewsEl = modal.querySelector('[class*="boost-sd__product-review"]') ||
                        modal.querySelector('[class*="review"]') ||
                        modal.querySelector('[class*="rating"]');
        // Insert stock + divider after reviews (or after Splitit if no reviews)
        var insertAfter = reviewsEl || modal.querySelector('.milano-qv-splitit') || priceEl;
        if (insertAfter) {
          var stock = document.createElement('div');
          stock.className = 'milano-qv-stock';
          stock.innerHTML = '<span class="milano-qv-stock-dot"></span> Item is in stock';

          var hr = document.createElement('hr');
          hr.className = 'milano-qv-divider';

          var parent = insertAfter.parentElement;
          var ref = insertAfter.nextSibling;
          parent.insertBefore(stock, ref);
          parent.insertBefore(hr, stock.nextSibling);
        }
      }

      // 5. Restyle detail link as primary CTA
      if (productHref.indexOf('customize=true') === -1) {
        detailLink.setAttribute('href', productHref + (productHref.indexOf('?') === -1 ? '?' : '&') + 'customize=true');
      }
      detailLink.className = 'milano-qv-cta';
      detailLink.textContent = 'Select Cap Size & Customize \u2192';

      // 6. Trust signals
      if (!modal.querySelector('.milano-qv-trust')) {
        var trust = document.createElement('div');
        trust.className = 'milano-qv-trust';
        trust.innerHTML =
          '<span>\u2726 Free 5-Day Returns</span>' +
          '<span>\u2726 Free Shipping $50+</span>' +
          '<span>\u2726 Handcrafted to Order</span>';
        detailLink.parentElement.appendChild(trust);
      }

      // 7. Tell DN to process the quick view modal
      try {
        document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
      } catch(e) {}

      // Update Splitit if DN changes the price (poll for DN render)
      if (productHandle) {
        var dnPollCount = 0;
        function updateSplititFromDN() {
          if (dnPollCount++ > 10) return;
          // Dynamic Pricing: look for non-strikethrough price DN rendered
          var dnSale = modal.querySelector('.limoniapps-discountninja-productprice-replaced, [data-token] :not(s):not(del)');
          var splititEl = modal.querySelector('.milano-qv-splitit');
          if (dnSale && splititEl) {
            var salePrice = extractPrice(dnSale);
            if (salePrice > 0) {
              var monthly = formatMoney(salePrice / 3);
              var logoUrl = getSplititLogoUrl();
              splititEl.innerHTML =
                'From ' + monthly + '/month with ' +
                '<img src="' + logoUrl + '" alt="Splitit" onerror="this.outerHTML=\'<strong style=color:#2fad4a>Splitit</strong>\'">' +
                ' <a href="https://www.splitit.com/" target="_blank" rel="noopener">Learn more</a>';
              console.log('[Milano] Splitit updated from DN sale price: $' + salePrice);
              return;
            }
          }
          setTimeout(updateSplititFromDN, 300);
        }
        setTimeout(updateSplititFromDN, 500);
      }

      modal.setAttribute('data-milano-qv-product', productHref);
      console.log('[Milano] Quick view enhanced: ' + productHref);
      return true;
    }

    function pollForModal(count) {
      if (count <= 0) return;
      if (!enhanceQuickView()) {
        setTimeout(function() { pollForModal(count - 1); }, 200);
      }
    }

    // DN prices: use official DN API to render in quick view
    // Dynamic Pricing: data-la-dn-price on price element
    // Quick View: add data-la-dn-product-handle + data-la-dn-product-quickview to container,
    // then dispatch la:dn:collection:updated so DN processes the modal.

    function extractHandleFromHref(href) {
      var match = (href || '').match(/\/products\/([^?#\/]+)/);
      return match ? match[1] : '';
    }

    document.addEventListener('click', function(e) {
      var t = e.target;
      for (var i = 0; i < 5 && t && t !== document; i++) {
        var cl = (t.className || '') + ' ' + (t.getAttribute('aria-label') || '');
        if (cl.toLowerCase().indexOf('quick view') !== -1 || cl.indexOf('quick-view') !== -1) {
          pollForModal(25);
          return;
        }
        t = t.parentElement;
      }
    }, true);
  })();

  // =========================================================================
  // START
  // =========================================================================

  // PDP-only: SC Options polling, panel building, price sync RAF loop.
  // These are the expensive operations (20s polling, perpetual RAF loop,
  // MutationObservers) that should NOT run on collection/home/cart pages.
  if (isPDP) {
    // Always start price sync (BNPL, ATC) — even without SC Options,
    // variant changes need Splitit/Klarna updates.
    setTimeout(syncATCPrice, 3500);

    waitForBoldOptions(function() {
      buildPanel();
      setTimeout(function() {
        if (!state.panelBuilt) buildPanel();
        syncATCPrice(); // safe to call again — guard prevents double-start
      }, 2000);
    });

    // =====================================================================
    // ATC SAFETY NET — Bold Clone fallback
    //
    // SC Options hides the original ATC button (bold_hidden + display:none)
    // and shows a cloned copy (bold_clone). If SC Options' JS hasn't
    // finished wiring the clone's event handler (slow mobile connection),
    // tapping ATC does nothing — no animation, no error, no cart add.
    //
    // This safety net catches that case:
    // 1. Listens for clicks on any .bold_clone button
    // 2. Starts a 3-second timer
    // 3. If theme:cart:added fires → normal flow worked, cancel timer
    // 4. If timer expires → force-submit the form as fallback
    //
    // Only activates on PDP. Zero impact when ATC works normally.
    // =====================================================================
    (function() {
      var safetyTimer = null;
      var safetyActive = false;

      function clearSafety() {
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        safetyActive = false;
      }

      // Cancel safety if cart add succeeded normally
      document.addEventListener('theme:cart:added', clearSafety);

      // Delegated click on bold_clone buttons
      document.addEventListener('click', function(e) {
        var btn = e.target.closest('button.bold_clone[type="submit"]');
        if (!btn || safetyActive) return;

        // Only activate if button is NOT loading (avoids double-submit)
        if (btn.classList.contains('is-loading') || btn.classList.contains('is-success')) return;

        safetyActive = true;
        safetyTimer = setTimeout(function() {
          // Check: did the cart drawer open? If so, ATC worked — bail out
          if (document.body.classList.contains('js-drawer-open-cart')) {
            clearSafety();
            return;
          }

          // Check: is the button in loading state? If so, fetch is in-flight — wait
          if (btn.classList.contains('is-loading')) {
            clearSafety();
            return;
          }

          // Safety net: force form submission
          var form = btn.closest('form[action*="/cart/add"]');
          if (form) {
            console.warn('[Milano] ATC safety net activated — forcing form submit');
            // Use Palo Alto's product-form if available
            var productForm = btn.closest('product-form');
            if (productForm && typeof productForm.onSubmitHandler === 'function') {
              var fakeEvent = { preventDefault: function() {} };
              try {
                productForm.onSubmitHandler(fakeEvent);
              } catch (err) {
                console.warn('[Milano] Safety net product-form submit failed, using native:', err);
                form.submit();
              }
            } else {
              form.submit();
            }
          }
          clearSafety();
        }, 3000);
      }, true); // useCapture to catch early
    })();
  }

  // Quick View (collection pages) and Cart Drawer strikethrough (all pages)
  // are self-contained IIFEs above — they always run regardless of isPDP.

})();